import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import api, { API } from "@/lib/api";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();

export default function Properties() {
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/houses").then((r) => setHouses(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <h1 className="font-display font-bold text-4xl text-[#0A2E26] tracking-tight">The Portfolio</h1>
        <p className="text-[#5C5C5C] mt-2 text-lg">Every property the fund is currently working on.</p>

        {loading ? (
          <p className="mt-10 text-[#5C5C5C]">Loading properties…</p>
        ) : houses.length === 0 ? (
          <div data-testid="empty-portfolio" className="mt-10 bg-white border border-[#E5E5E5] rounded-xl p-12 text-center">
            <p className="text-[#5C5C5C]">No properties yet. Check back soon as the fund acquires its first homes.</p>
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
            {houses.map((h, i) => {
              const profit = (h.sale_price || 0) - (h.purchase_price || 0) - (h.rehab_cost || 0);
              return (
                <motion.div
                  key={h.id}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`property-card-${h.id}`}
                  className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-transform"
                >
                  <div className="h-44 bg-[#0A2E26]/5 overflow-hidden">
                    <img
                      src={h.image || "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
                      alt={h.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-display font-bold text-lg text-[#0A2E26]">{h.name}</h3>
                        <p className="text-sm text-[#5C5C5C]">{h.address}</p>
                      </div>
                      <StatusBadge status={h.status} />
                    </div>

                    {h.status === "rehab" && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-[#5C5C5C] mb-1">
                          <span>Rehab progress</span>
                          <span>{h.rehab_progress || 0}%</span>
                        </div>
                        <Progress value={h.rehab_progress || 0} className="h-2" />
                      </div>
                    )}

                    {(h.photos && h.photos.length > 0) && (
                      <div className="mt-4 border-t border-[#E5E5E5] pt-4" data-testid={`timeline-${h.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[#5C5C5C] mb-2">Rehab timeline</p>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {[...h.photos].sort((a, b) => {
                            const order = { before: 0, progress: 1, after: 2 };
                            return (order[a.label] ?? 1) - (order[b.label] ?? 1);
                          }).map((p) => (
                            <div key={p.id} className="relative shrink-0 rounded-lg overflow-hidden border border-[#E5E5E5]">
                              <img src={`${API}/files/${p.storage_path}`} alt={p.label} className="w-28 h-20 object-cover" />
                              <span className="absolute bottom-1 left-1 text-[10px] font-semibold text-white bg-black/55 px-1.5 py-0.5 rounded capitalize">{p.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-2 gap-y-2 text-sm border-t border-[#E5E5E5] pt-4">
                      <span className="text-[#5C5C5C]">Cycle</span>
                      <span className="text-right font-medium text-[#0A2E26]">#{h.cycle}</span>
                      <span className="text-[#5C5C5C]">Purchase</span>
                      <span className="text-right font-medium text-[#0A2E26]">{fmt(h.purchase_price)}</span>
                      <span className="text-[#5C5C5C]">Rehab</span>
                      <span className="text-right font-medium text-[#0A2E26]">{fmt(h.rehab_cost)}</span>
                      {h.status === "sold" && (
                        <>
                          <span className="text-[#5C5C5C]">Sold for</span>
                          <span className="text-right font-medium text-[#0A2E26]">{fmt(h.sale_price)}</span>
                          <span className="text-[#5C5C5C]">Profit</span>
                          <span className="text-right font-semibold text-[#059669]">{fmt(profit)}</span>
                        </>
                      )}
                    </div>

                    <Link to={`/properties/${h.id}`} data-testid={`view-details-${h.id}`}
                      className="mt-4 block text-center text-sm font-semibold text-[#0A2E26] border border-[#0A2E26]/20 rounded-md py-2.5 hover:bg-[#0A2E26] hover:text-white transition-colors">
                      View details
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
